// One derivation of "what needs attention", shared by the overview and the
// issues queue so both always agree. Built only from real backend state
// (documents' review reasons and failures, per-section readiness, and the
// findings of the report audits on the drafts).
import type { DossierDocument, InputReadiness, ReportAudits } from "./api/types";
import { label, w } from "./i18n";

export type Severity = "high" | "medium" | "low";

export interface Issue {
  id: string;
  severity: Severity;
  scope: string;           // "A.2", "D.2" or the inputs scope
  message: string;
  kind: "document_failed" | "document_review" | "section_missing" | "section_pending" | "section_partial" | "audit";
  documentId?: string;
  sectionCode?: string;
}

const ORDER: Record<Severity, number> = { high: 0, medium: 1, low: 2 };

export function deriveIssues(documents: DossierDocument[] = [], readiness?: InputReadiness, audits?: ReportAudits): Issue[] {
  const issues: Issue[] = [];
  for (const document of documents) {
    if (document.status === "failed") {
      issues.push({ id: `fail:${document.id}`, severity: "high", scope: w("issues.input"), kind: "document_failed",
        documentId: document.id, message: w("issues.docFailed", { name: document.display_name,
          message: document.latest_job?.error_message || w("docs.failedShort") }) });
    }
    if (document.status === "needs_review") {
      document.review_reasons.forEach((reason, index) => issues.push({
        id: `review:${document.id}:${index}`, severity: "medium", scope: w("issues.input"), kind: "document_review",
        documentId: document.id, message: w("issues.docReview", { name: document.display_name, message: reason.message }) }));
    }
  }
  for (const chapter of readiness?.chapters || []) {
    for (const section of chapter.sections) {
      for (const requirement of section.requirements) {
        if (requirement.state === "satisfied") continue;
        const pending = requirement.state === "pending";
        issues.push({
          id: `req:${section.code}:${requirement.key}`, scope: section.code, sectionCode: section.code,
          documentId: pending ? requirement.documents[0]?.id : undefined,
          severity: requirement.required && !pending ? "high" : requirement.required ? "medium" : "low",
          kind: pending ? "section_pending" : requirement.required ? "section_missing" : "section_partial",
          message: w(pending ? "issues.sectionPending" : requirement.required ? "issues.sectionMissing" : "issues.sectionPartial",
            { label: label("req", requirement.key, requirement.label) }),
        });
      }
    }
  }
  // A KO of a check on a draft is a blocking point; an untraced small figure is to review.
  // Sections not drafted yet are already reported by readiness above.
  for (const row of Object.values(audits?.audits || {}).flat()) {
    if (!row.from_check || row.result === "ok" || row.result === "na") continue;
    if (row.result === "review" && row.audit !== "coherence") continue;
    const what = row.audit === "regulatory" ? `${row.reference} ${row.item}` : row.label;
    issues.push({ id: `audit:${row.id}`, severity: row.result === "ko" ? "high" : "medium", scope: row.section || w("issues.input"),
      sectionCode: row.section || undefined, kind: "audit",
      message: w("issues.audit", { audit: w(`audits.tab.${row.audit}`), what, comment: row.comment }) });
  }
  return issues.sort((a, b) => ORDER[a.severity] - ORDER[b.severity]);
}
